import { Injectable, isDevMode } from '@angular/core';
import { DataLayerEntry, DataLayerObject } from '../../types';

export const GOOGLE_TAG_MANAGER_SCRIPT_SRC = (trackingCode: string) => `https://www.googletagmanager.com/gtm.js?id=${trackingCode}`;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private isInitialized = false;

  /**
   * Wrapper on console.warn, only show warnings in dev mode
   * @param args arguments to pass to console.warn
   */
  private warn(...args: any[]) {
    if (isDevMode()) console.warn(...args);
  }

  constructor() {
    // 2. Intercept the global window level pushes right when this class loads
    this.setupGlobalFilter();
  }

  private setupGlobalFilter() {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];

    // Save the true native array push function
    const nativePush = window.dataLayer.push;

    // Set of events to intercept and block
    const blockedEvents = new Set([
      'gtm.click', // GTM All Elements Click
      'gtm.linkClick', // GTM Just Links Click
      'gtm.historyChange', // GTM Virtual Page Change
      'gtm.formSubmit', // GA4 Form Submission
      'page_view', // GA4 Page View
      'click', // GA4 Outbound Click
      'scroll', // GA4 Scroll (90%)
      'view_search_results', // GA4 Site Search
      'file_download', // GA4 File Download
      'form_start', // GA4 Form Interaction Start
      'form_submit', // GA4 Form Submission
      'video_start', // GA4 YouTube Video Start
      'video_progress', // GA4 YouTube Video Milestones
      'video_complete', // GA4 YouTube Video End
    ]);

    // Overwrite the global window push method directly with matching return types
    window.dataLayer.push = function (...args: DataLayerEntry[]): number {
      for (const argument of args) {
        // Intercept and destroy any matching blocked events
        if (argument && typeof argument === 'object' && blockedEvents.has(argument.event)) {
          // Return current array length to match the native Array.push return specification
          return window.dataLayer.length;
        }
      }
      // Pass all legitimate events to the true array push method
      return nativePush.apply(window.dataLayer, args);
    };
  }

  /**
   * Set up the dataLayer and load the GTM script
   * @param trackingCode GTM tracking code
   */
  initialize(trackingCode?: string) {
    if (this.isInitialized) throw new Error('Tried to initialize GoogleAnalyticsService twice.');

    window.dataLayer = window.dataLayer || [];
    this.isInitialized = true;

    // Push initialization event
    this.trackevent('gtm.js', { 'gtm.start': Date.now() });

    if (!trackingCode) return this.warn('No GTM tracking code provided, analytics disabled.');

    // Inject the GTM script into the document head
    const script = document.createElement('script');
    script.async = true;
    script.src = GOOGLE_TAG_MANAGER_SCRIPT_SRC(trackingCode);
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  /**
   * Push an entry onto the dataLayer
   * @param entry entry to push onto dataLayer
   */
  push(entry: DataLayerEntry) {
    if (!this.isInitialized) return this.warn('Tried to push to GTM before initialization.', entry);
    window.dataLayer.push(entry);
  }

  /**
   * Record an analytics event
   * @param event event name
   * @param parameters extra event parameters
   */
  trackevent(event: DataLayerEntry, parameters: DataLayerObject = {}) {
    if (typeof event === 'string') event = { event };
    this.push({ ...event, ...parameters });
  }
}
